import { useMemo, useState } from 'react';
import {
  Plus,
  AlertTriangle,
  CalendarClock,
  Activity,
  CheckCircle2,
  Users,
  ListTodo,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRole } from '@/hooks/useRole';
import { useStaff } from '@/features/staff/useStaff';
import { TaskCard } from './TaskCard';
import { TaskModal } from './TaskModal';
import { useMyTasks, useAllTasks } from './useTasks';
import { bucketTasks } from './taskBuckets';
import { computeTaskMetrics, summarizeByAssignee, taskSlaStatus } from './taskMetrics';
import { BUCKET_ORDER, BUCKET_LABELS, type TaskWithClient } from './types';

type Tab = 'mine' | 'team';

const BUCKET_TONE: Record<string, string> = {
  overdue: 'text-expense',
  today: 'text-primary',
  next_48h: 'text-foreground',
  later: 'text-muted-foreground',
  no_due_date: 'text-muted-foreground',
};

export function TasksPage() {
  const { isManager } = useRole();
  const [tab, setTab] = useState<Tab>('mine');
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Tarefas</h1>
          <p className="text-sm text-muted-foreground">
            Acompanhe prazos e SLA das suas tarefas e da equipe.
          </p>
        </div>
        <Button
          onClick={() => setModalOpen(true)}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          <Plus size={16} className="mr-2" />
          Nova tarefa
        </Button>
      </div>

      {isManager && (
        <div className="flex gap-1 rounded-lg border border-border bg-white p-1">
          <button
            onClick={() => setTab('mine')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              tab === 'mine'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-secondary'
            }`}
          >
            <ListTodo size={15} /> Minhas tarefas
          </button>
          <button
            onClick={() => setTab('team')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              tab === 'team'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-secondary'
            }`}
          >
            <Users size={15} /> Equipe
          </button>
        </div>
      )}

      {tab === 'mine' || !isManager ? <MyTasksView /> : <TeamDashboard />}

      <TaskModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}

function MyTasksView() {
  const { data: tasks = [], isLoading } = useMyTasks();
  const buckets = useMemo(() => bucketTasks(tasks), [tasks]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg border border-border bg-muted" />
        ))}
      </div>
    );
  }

  const hasOpen = BUCKET_ORDER.some((b) => buckets[b].length > 0);

  if (!hasOpen && buckets.recentlyCompleted.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-white p-12 text-center">
        <CheckCircle2 size={32} className="mx-auto mb-2 text-income" />
        <p className="font-medium text-foreground">Nenhuma tarefa pendente</p>
        <p className="text-sm text-muted-foreground">Tudo em dia. Crie uma nova tarefa quando precisar.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {BUCKET_ORDER.map((b) =>
        buckets[b].length === 0 ? null : (
          <section key={b}>
            <h2 className={`mb-2 flex items-center gap-2 text-sm font-semibold ${BUCKET_TONE[b]}`}>
              {BUCKET_LABELS[b]}
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-normal text-muted-foreground">
                {buckets[b].length}
              </span>
            </h2>
            <div className="space-y-1.5">
              {buckets[b].map((t) => (
                <TaskCard key={t.id} task={t} />
              ))}
            </div>
          </section>
        ),
      )}
      {buckets.recentlyCompleted.length > 0 && (
        <section>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-income">
            Concluídas hoje
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-normal text-muted-foreground">
              {buckets.recentlyCompleted.length}
            </span>
          </h2>
          <div className="space-y-1.5">
            {buckets.recentlyCompleted.map((t) => (
              <TaskCard key={t.id} task={t} compact />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

interface KpiProps {
  icon: typeof AlertTriangle;
  label: string;
  value: number;
  tone: 'expense' | 'primary' | 'amber' | 'income';
}

function Kpi({ icon: Icon, label, value, tone }: KpiProps) {
  const toneClasses = {
    expense: 'text-expense',
    primary: 'text-primary',
    amber: 'text-amber-600',
    income: 'text-income',
  } as const;
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon size={14} /> {label}
      </div>
      <div className={`mt-1 font-brand text-2xl font-bold tabular-nums ${toneClasses[tone]}`}>
        {value}
      </div>
    </div>
  );
}

function TeamDashboard() {
  const { data: tasks = [], isLoading } = useAllTasks();
  const { data: staff = [] } = useStaff();

  const staffByUserId = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of staff) {
      if (s.user_id) map.set(s.user_id, s.name);
    }
    return map;
  }, [staff]);

  const metrics = useMemo(() => computeTaskMetrics(tasks), [tasks]);
  const byAssignee = useMemo(() => summarizeByAssignee(tasks), [tasks]);
  const overdueTasks = useMemo(
    () =>
      tasks
        .filter((t) => taskSlaStatus(t) === 'overdue')
        .sort((a, b) => {
          const da = a.due_at ? new Date(a.due_at).getTime() : 0;
          const db = b.due_at ? new Date(b.due_at).getTime() : 0;
          return da - db;
        }),
    [tasks],
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl border border-border bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi icon={AlertTriangle} label="Atrasadas" value={metrics.overdue} tone="expense" />
        <Kpi icon={CalendarClock} label="Vencem hoje" value={metrics.dueToday} tone="amber" />
        <Kpi icon={Activity} label="Em andamento" value={metrics.inProgress} tone="primary" />
        <Kpi icon={CheckCircle2} label="Concluídas (7d)" value={metrics.completedLast7d} tone="income" />
      </div>

      {/* Por colaborador */}
      <section className="rounded-xl border border-border bg-white">
        <header className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Users size={16} className="text-primary" />
          <h2 className="font-brand font-semibold text-foreground">Carga por colaborador</h2>
        </header>
        {byAssignee.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            Nenhuma tarefa cadastrada.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {byAssignee.map((s) => {
              const name = s.assigneeUserId
                ? (staffByUserId.get(s.assigneeUserId) ?? 'Colaborador desconhecido')
                : 'Sem responsável';
              return (
                <li
                  key={s.assigneeUserId ?? '__none__'}
                  className="flex items-center justify-between gap-3 px-4 py-3"
                >
                  <span className="truncate text-sm font-medium text-foreground">{name}</span>
                  <div className="flex shrink-0 items-center gap-2 text-xs">
                    {s.overdue > 0 && (
                      <span className="rounded-full bg-expense/10 px-2 py-0.5 font-medium text-expense">
                        {s.overdue} atrasada(s)
                      </span>
                    )}
                    {s.dueToday > 0 && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700">
                        {s.dueToday} hoje
                      </span>
                    )}
                    <span className="rounded-full bg-secondary px-2 py-0.5 font-medium text-muted-foreground">
                      {s.open} aberta(s)
                    </span>
                    <span className="rounded-full bg-income/10 px-2 py-0.5 font-medium text-income">
                      {s.completedLast7d} ✓ 7d
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Atrasadas */}
      <section>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-expense">
          <AlertTriangle size={15} /> Tarefas atrasadas
          <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-normal text-muted-foreground">
            {overdueTasks.length}
          </span>
        </h2>
        {overdueTasks.length === 0 ? (
          <div className="rounded-xl border border-border bg-white p-8 text-center text-sm text-muted-foreground">
            Nenhuma tarefa atrasada. Equipe em dia.
          </div>
        ) : (
          <div className="space-y-1.5">
            {overdueTasks.map((t: TaskWithClient) => (
              <TaskCard key={t.id} task={t} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
